import React, { useCallback, useEffect, useMemo } from 'react';
import { useMachine } from '@xstate/react';
import cogoToast from 'cogo-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { rgba } from 'polished';

import { createCard, getCards } from '~/api/routes/leadad/context-cards';
import {
  ContextCard,
  ContextCardForm,
} from '~/api/models/leadads/context-card';
import Button from '~/theseus/components/Button';

import { sendErrorService } from '~/services/error';

import EnabledSwitch from '../../EnabledSwitch';

import Section from '../Section';
import Header from '../Header';
import Description from '../Description';
import Selector from './Selector';
import Form from './Form';
import machine from './machine';
import { useAuthSelector } from '~/theseus/containers/Auth';
import { usePageQuery } from '~/theseus/hooks/queries';

function push<T>(a: T) {
  return (b: T[]) => [...b, a];
}

const ModifiedHeader = styled(Header)`
  h2 span {
    padding-left: 3px;
    font-weight: 400;
    color: ${rgba('#000', 0.5)};
  }
`;

type CCRequestData = {
  form: ContextCardForm;
  pageId: string;
};

interface Props {
  onChange: (cardId: string | null) => void;
  propertyImages: string[];
  onReady: (isReady: boolean) => void;
}

function ContextCards(props: Props) {
  const { onChange, propertyImages, onReady } = props;

  const [pageId] = usePageQuery();
  const queryClient = useQueryClient();
  const [
    {
      value,
      context: { selectedCard },
    },
    send,
  ] = useMachine(machine, {
    devTools: process.env.NODE_ENV === 'development',
    actions: {
      onReady: () => onReady(true),
      onNotReady: () => onReady(false),
      onCardSelected: (ctx) => onChange(ctx.selectedCard),
    },
  });
  const toggleEnabled = useCallback(() => send('CONTEXT_CARDS.toggleEnabled'), [
    send,
  ]);
  const toggleModes = useCallback(() => send('CONTEXT_CARDS.toggleModes'), [
    send,
  ]);
  const selectCard = useCallback(
    (id: string) => send({ type: 'CONTEXT_CARDS.select', cardId: id }),
    [send],
  );
  const noContextCards = useCallback(() => send('CONTEXT_CARDS.noData'), [
    send,
  ]);

  const { data: contextCards, isFetching, isFetched } = useQuery(
    ['leadad', 'context-cards', pageId],
    () => getCards(pageId),
    {
      initialData: [] as ContextCard[],
      enabled: !!pageId,
    },
  );

  const {
    mutate, isLoading: isAddingCard, isSuccess, data,
  } = useMutation(
    (args: CCRequestData) => createCard(args.pageId, args.form),
    {
      onError: (errResp: ResponseWith<string>) => {
        const errorId = sendErrorService(
          new Error('Error creating ContextCard'),
          'CONTEXT_CARDS',
          errResp.data,
        );
        cogoToast.error(
          `There was a problem creating your welcome message. Please try again. Error id: ${errorId}`,
        );
      },
    },
  );

  useEffect(() => {
    if (isFetched) {
      if (!contextCards || contextCards.length === 0) {
        noContextCards();
      } else {
        selectCard(contextCards[0].id);
      }
    }
  }, [isFetched, contextCards, noContextCards, selectCard]);

  const create = useCallback(
    (form: ContextCardForm) => {
      mutate(
        { pageId, form },
        {
          onSuccess: (newCard) => {
            queryClient.setQueryData(
              ['leadad', 'context-cards', pageId],
              push(newCard),
            );
          },
        },
      );
    },
    [mutate, pageId, queryClient],
  );

  useEffect(() => {
    if (isSuccess && data) {
      cogoToast.success('Welcome message successfully created');
      selectCard(data.id);
    }
  }, [data, isSuccess, selectCard]);

  const isDisabled = value === 'disabled';
  const isEnabled = !isDisabled;
  const isBusy = isFetching || isAddingCard;

  const currentPane = useMemo(() => {
    switch (value) {
      case 'initializing':
      case 'select':
        return (
          <div data-testid="context-card-container">
            <Description>Create New Welcome Message</Description>
            <Button
              onClick={toggleModes}
              disabled={value === 'initializing'}
              size="md"
              css={css`
                margin: 20px;
              `}
            >
              Create New Welcome Message
            </Button>
            <Description>Or Use a Saved Welcome Message</Description>
            <Selector
              options={contextCards}
              selected={selectedCard}
              isLoading={value === 'initializing'}
              onChange={selectCard}
            />
          </div>
        );
      case 'create':
      case 'createFirst':
        return (
          <Form
            showBackButton={value === 'create'}
            propertyImages={propertyImages}
            onCancel={toggleModes}
            onCreate={create}
            isCreatingCard={isAddingCard}
          />
        );
      case 'disabled':
      default:
        return null;
    }
  }, [
    value,
    isAddingCard,
    contextCards,
    propertyImages,
    selectCard,
    selectedCard,
    create,
    toggleModes,
  ]);

  return (
    <Section>
      <ModifiedHeader>
        <h2>
          Welcome Message
          <span>(optional)</span>
        </h2>
        <EnabledSwitch
          onClick={toggleEnabled}
          isEnabled={isEnabled}
          disabled={isBusy}
          label="include welcome message"
        />
      </ModifiedHeader>
      {currentPane}
    </Section>
  );
}

export default React.memo(ContextCards);
